import { useEffect, useRef } from 'react';
import { io } from 'socket.io-client';
import he from 'he';
import {
  baseMediaUrl,
  baseUrl,
  getRequest,
  postRequest,
  refreshAuthToken,
  requestNotificationPermission,
  showNotification,
} from '../utils/services';
import { useMessageStore } from '../stores/useMessageStore';
import { useSocketStore } from '../stores/useSocketStore';
import { useTicketStore } from '../stores/useTicketStore';
import { useUIStore } from '../stores/useUIStore';
import {
  areChatsEqual,
  buildMessageAccessFromTicketState,
  isSameTicket,
  shouldRefetchMessagesForTicketState,
  updateUserChatsWithNewMessage,
} from '../utils/chatState';

const resolveSocketUrl = () => {
  if (process.env.NODE_ENV === 'development') {
    return process.env.REACT_APP_SOCKET_URL || 'https://chat.telein.com.br';
  }

  return window.location.origin;
};

const resolveWaitingQueueVisibility = (ticket, access, userSectors = []) => {
  if (typeof access?.canViewInWaitingQueue === 'boolean') {
    return access.canViewInWaitingQueue;
  }

  const sectorId = Number(ticket?.setor_id);

  if (ticket?.setor_id == null || sectorId === 0 || Number.isNaN(sectorId)) {
    return true;
  }

  return userSectors.some(
    (sector) => Number(sector?.id_setor) === sectorId,
  );
};

const hasSameTicketAssignment = (chat, snapshot) => (
  Number(chat?.status ?? 0) === Number(snapshot?.status ?? 0)
  && Number(chat?.usuario_id ?? 0) === Number(snapshot?.usuario_id ?? 0)
  && Number(chat?.setor_id ?? 0) === Number(snapshot?.setor_id ?? 0)
);

export function useChatSocket({
  user,
  socket,
  currentChat,
  internalChats,
  openTickets,
  userChatsQueue,
  recipientUser,
  onlineUsers,
  logoutUser,
  fetchMediaUrls,
  fetchMessages,
  handleScrollChatToBottom,
  playNotificationSound,
  playOpenChatNotification,
  setErrorMessage,
}) {
  const setSocket = useSocketStore((state) => state.setSocket);
  const setOnlineUsers = useSocketStore((state) => state.setOnlineUsers);
  const setIsRecipientOnline = useSocketStore((state) => state.setIsRecipientOnline);
  const setSessionConflict = useSocketStore((state) => state.setSessionConflict);

  const setMessages = useMessageStore((state) => state.setMessages);
  const setQuotedMessages = useMessageStore((state) => state.setQuotedMessages);
  const setMessageAccess = useMessageStore((state) => state.setMessageAccess);
  const setOfficialApiTemplates = useMessageStore((state) => state.setOfficialApiTemplates);

  const isRecordingAudio = useUIStore((state) => state.isRecordingAudio);
  const showAlert = useUIStore((state) => state.showAlert);

  const setCurrentChat = useTicketStore((state) => state.setCurrentChat);
  const upsertGeneralChat = useTicketStore((state) => state.upsertGeneralChat);
  const updateTicketMetadata = useTicketStore((state) => state.updateTicketMetadata);
  const setTotalUserChatsQueue = useTicketStore((state) => state.setTotalUserChatsQueue);
  const setUserChatsQueue = useTicketStore((state) => state.setUserChatsQueue);
  const syncWaitingQueueTicket = useTicketStore((state) => state.syncWaitingQueueTicket);
  const syncTransferredOpenTicket = useTicketStore(
    (state) => state.syncTransferredOpenTicket,
  );
  const setTotalOpenTickets = useTicketStore((state) => state.setTotalOpenTickets);
  const setOpenTickets = useTicketStore((state) => state.setOpenTickets);
  const resetWaitingQueue = useTicketStore((state) => state.resetWaitingQueue);
  const getUserChatsQueue = useTicketStore((state) => state.getUserChatsQueue);
  const updateInternalChatsWithNewMessage = useTicketStore(
    (state) => state.updateInternalChatsWithNewMessage,
  );
  const hasConnectedRef = useRef(false);

  const closeUnavailableCurrentChat = (message) => {
    setCurrentChat(null);
    setMessages([]);
    setMessageAccess();
    showAlert(message, 'warning', 5000);
  };

  const reconcileCurrentTicket = async () => {
    const activeChat = useTicketStore.getState().currentChat;

    if (!activeChat?.sender || !activeChat?.fromMsg || activeChat?.internalChat) {
      return;
    }

    try {
      const ticketState = await getRequest(
        `${baseUrl}/chats/ticketDetails/`
        + `${encodeURIComponent(activeChat.sender)}/`
        + `${encodeURIComponent(activeChat.fromMsg)}`,
      );
      const latestCurrentChat = useTicketStore.getState().currentChat;

      if (
        !isSameTicket(latestCurrentChat, activeChat)
        || !hasSameTicketAssignment(latestCurrentChat, activeChat)
      ) return;

      if (ticketState?.error) {
        if (ticketState.code === 'TICKET_OWNERSHIP_CONFLICT') {
          closeUnavailableCurrentChat('Este chamado foi assumido por outro atendente.');
          return;
        }

        if (ticketState.code === 'RESOURCE_OUT_OF_SCOPE') {
          closeUnavailableCurrentChat('Este chamado não está mais disponível na sua fila.');
          return;
        }

        setErrorMessage(
          'Não foi possível atualizar o chamado selecionado após a reconexão.',
        );
        return;
      }

      const wasWaiting = Number(activeChat.status) === 1;
      const wasOwnedByCurrentUser = Number(activeChat.status) === 2
        && Number(activeChat.usuario_id) === Number(user?.id);
      const claimedByAnotherAttendant = wasWaiting
        && Number(ticketState?.status) === 2
        && Number(ticketState?.usuario_id) !== Number(user?.id);
      const lostWaitingQueueAccess = wasWaiting
        && Number(ticketState?.status) === 1
        && ticketState?.canViewInWaitingQueue === false;
      const transferredAwayFromCurrentUser = wasOwnedByCurrentUser
        && (
          Number(ticketState?.status) !== 2
          || Number(ticketState?.usuario_id) !== Number(user?.id)
        );
      const closedWhileDisconnected = (wasWaiting || wasOwnedByCurrentUser)
        && Number(ticketState?.status) === 3;

      if (claimedByAnotherAttendant) {
        closeUnavailableCurrentChat('Este chamado foi assumido por outro atendente.');
        return;
      }

      if (transferredAwayFromCurrentUser) {
        closeUnavailableCurrentChat('Este chamado foi transferido para outro atendente.');
        return;
      }

      if (lostWaitingQueueAccess || closedWhileDisconnected) {
        closeUnavailableCurrentChat('Este chamado não está mais disponível na sua fila.');
        return;
      }

      let didApplyTicketState = false;

      setCurrentChat((previousCurrentChat) => {
        if (
          !isSameTicket(previousCurrentChat, activeChat)
          || !hasSameTicketAssignment(previousCurrentChat, activeChat)
        ) {
          return previousCurrentChat;
        }

        didApplyTicketState = true;
        return { ...previousCurrentChat, ...ticketState };
      });

      if (didApplyTicketState) {
        setMessageAccess(buildMessageAccessFromTicketState(ticketState));
      }
    } catch (error) {
      setErrorMessage(
        'Não foi possível atualizar o chamado selecionado após a reconexão.',
      );
    }
  };

  const fetchSignedMediaUrl = async (mediaKey) => {
    try {
      const response = await postRequest(
        `${baseUrl}/messages/sign-media-url`,
        { key: mediaKey },
      );

      if (response.error) {
        setErrorMessage(
          'Não foi possível carregar mídia da mensagem! Entre em contato com o suporte.',
        );
        return null;
      }

      return response;
    } catch (error) {
      setErrorMessage('Erro ao buscar mídia da mensagem');
      return null;
    }
  };

  const mergeChatMetadataWithMessage = (baseChat, messagePayload) => {
    if (!baseChat) {
      return messagePayload;
    }

    return {
      ...baseChat,
      ...messagePayload,
      status: messagePayload?.status ?? baseChat.status,
      chat_tags: messagePayload?.chat_tags ?? baseChat.chat_tags,
      nome_setor: messagePayload?.nome_setor ?? baseChat.nome_setor,
      setor_id: messagePayload?.setor_id ?? messagePayload?.setorId ?? baseChat.setor_id,
      usuario_id: messagePayload?.usuario_id ?? messagePayload?.userId ?? baseChat.usuario_id,
      nome: messagePayload?.nome ?? baseChat.nome,
      customName: messagePayload?.customName ?? baseChat.customName,
      profilePic: messagePayload?.profilePic ?? baseChat.profilePic,
      remoteJidAlt: messagePayload?.remoteJidAlt ?? baseChat.remoteJidAlt,
      lead_origem: messagePayload?.lead_origem ?? baseChat.lead_origem,
    };
  };

  useEffect(() => {
    if (!user?.id) return undefined;

    const socketURL = resolveSocketUrl();
    const newSocket = io(socketURL, {
      auth: (callback) => callback({
        token: localStorage.getItem('accessToken'),
      }),
      path: '/socket.io/',
      transports: ['websocket'],
      reconnection: true,
      reconnectionAttempts: 5,
      reconnectionDelay: 3000,
    });

    setSocket(newSocket);

    return () => {
      newSocket.disconnect();
    };
  }, [user?.id, setSocket]);

  useEffect(() => {
    if (!socket) return undefined;

    const onConnect = async () => {
      socket.emit('addNewUser', user?.id, user?.senders, user?.ambiente);

      if (hasConnectedRef.current) {
        resetWaitingQueue({ preserveItems: true });
        const queueResult = await getUserChatsQueue(user, setErrorMessage);
        await reconcileCurrentTicket();

        if (!queueResult?.error && !queueResult?.stale) {
          document.getElementById('scrollable-main')?.scrollTo({ top: 0 });
        }
      }

      hasConnectedRef.current = true;
    };

    const onReconnectAttempt = (attempt) => {
      showAlert(`Tentando reconectar: ${attempt} tentativa de 5.`, 'warning', 2000);
    };

    const onReconnect = () => {
      showAlert('Conexão reestabelecida com sucesso.', 'success', 5000);
    };

    const onReconnectFailed = () => {
      setErrorMessage(
        'Todas as tentativas de reconexão falharam. Verifique sua conexão com a internet e reinicie a página',
      );
    };

    const onConnectError = async (error) => {
      const authErrorCodes = ['INVALID_TOKEN', 'MISSING_TOKEN'];

      if (!authErrorCodes.includes(error?.data?.code)) return;

      const refreshedToken = await refreshAuthToken();

      if (typeof refreshedToken === 'string') {
        socket.connect();
        return;
      }

      logoutUser();
    };

    const onGetOnlineUsers = (res) => {
      setOnlineUsers(res);
    };

    const onForceLogout = () => {
      logoutUser();
    };

    socket.on('connect', onConnect);
    socket.on('reconnect_attempt', onReconnectAttempt);
    socket.on('reconnect', onReconnect);
    socket.on('reconnect_failed', onReconnectFailed);
    socket.on('connect_error', onConnectError);
    socket.on('getOnlineUsers', onGetOnlineUsers);
    socket.on('forceLogout', onForceLogout);

    return () => {
      socket.off('connect', onConnect);
      socket.off('reconnect_attempt', onReconnectAttempt);
      socket.off('reconnect', onReconnect);
      socket.off('reconnect_failed', onReconnectFailed);
      socket.off('connect_error', onConnectError);
      socket.off('getOnlineUsers', onGetOnlineUsers);
      socket.off('forceLogout', onForceLogout);
    };
  }, [
    getUserChatsQueue,
    logoutUser,
    resetWaitingQueue,
    setErrorMessage,
    setOnlineUsers,
    showAlert,
    socket,
    user,
  ]);

  useEffect(() => {
    if (!socket) return undefined;

    const onReceiveInternalMessage = async (message) => {
      let messageWithMedia = { ...message };

      if (message.mediaUrl) {
        const signedMediaUrl = await fetchSignedMediaUrl(message.mediaUrl);
        if (signedMediaUrl) {
          messageWithMedia = { ...messageWithMedia, mediaUrl: signedMediaUrl };
        }
      }

      if (message.quotedMsgId) {
        const quotedMessageResponse = await getRequest(
          `${baseUrl}/messages/internalMessage/${message.quotedMsgId}`,
        );

        setQuotedMessages((prevQuotedMessages) => [
          ...prevQuotedMessages,
          quotedMessageResponse,
        ]);
      }

      if (message.chatId === currentChat?.id) {
        setMessages((prev) => [messageWithMedia, ...prev]);
        setTimeout(handleScrollChatToBottom, 500);
      }

      await updateInternalChatsWithNewMessage({
        currentChatId: currentChat?.id,
        newMessage: messageWithMedia,
        setErrorMessage,
        userId: user.id,
      });

      const chatFromReceivedMessage = internalChats.find((chat) => chat.id === message.chatId);

      showNotification(`Você recebeu uma nova mensagem de ${chatFromReceivedMessage?.chat_name || chatFromReceivedMessage?.memberName}`, {
        body: he.decode(message?.body) || 'Nova mensagem',
        icon: 'https://chat.telein.com.br/assets/images/telein-logo-att.png',
      });

      requestNotificationPermission();
    };

    const onUpdateEditedMessageBody = (updatedMessage, userId) => {
      if (updatedMessage.chatId === currentChat?.id && user.id !== userId) {
        setMessages((prevMessages) => prevMessages.map((msg) => (msg.id === updatedMessage.id
          ? { ...msg, body: updatedMessage.body, isEdited: 1 }
          : msg)));
      }
    };

    const onUpdateDeletedMessage = (idMsg, currentChatId, userId) => {
      if (currentChatId === currentChat?.id && user.id !== userId) {
        setMessages((prevMessages) => prevMessages
          .map((msg) => (msg.id === idMsg
            ? { ...msg, isDeleted: 1 } : msg)));
      }
    };

    socket.on('receiveInternalMessage', onReceiveInternalMessage);
    socket.on('updateEditedMessageBody', onUpdateEditedMessageBody);
    socket.on('updateDeletedMessage', onUpdateDeletedMessage);

    return () => {
      socket.off('receiveInternalMessage', onReceiveInternalMessage);
      socket.off('updateEditedMessageBody', onUpdateEditedMessageBody);
      socket.off('updateDeletedMessage', onUpdateDeletedMessage);
    };
  }, [
    currentChat?.id,
    handleScrollChatToBottom,
    internalChats,
    setMessages,
    setQuotedMessages,
    socket,
    updateInternalChatsWithNewMessage,
    user?.id,
  ]);

  useEffect(() => {
    if (!socket) return undefined;

    const onTicketStateChanged = async (ticketState) => {
      if (!ticketState?.sender || !ticketState?.fromMsg) return;

      const activeChat = useTicketStore.getState().currentChat;
      const previousAccess = useMessageStore.getState().messageAccess;
      const isCurrentTicket = isSameTicket(activeChat, ticketState);
      const wasWaiting = Number(activeChat?.status) === 1;
      const wasUnassignedLead = Number(activeChat?.status) === 0
        && (activeChat?.usuario_id == null || Number(activeChat.usuario_id) === 0);
      const wasOwnedByCurrentUser = Number(activeChat?.status) === 2
        && Number(activeChat?.usuario_id) === Number(user?.id);
      const claimedByAnotherAttendant = isCurrentTicket
        && (wasWaiting || wasUnassignedLead)
        && Number(ticketState.status) === 2
        && Number(ticketState.usuario_id) !== Number(user?.id);
      const lostWaitingQueueAccess = isCurrentTicket
        && wasWaiting
        && Number(ticketState.status) === 1
        && ticketState.canViewInWaitingQueue === false;
      const transferredAwayFromCurrentUser = isCurrentTicket
        && wasOwnedByCurrentUser
        && (
          Number(ticketState.status) !== 2
          || Number(ticketState.usuario_id) !== Number(user?.id)
        );

      updateTicketMetadata(ticketState, user?.id);

      if (
        claimedByAnotherAttendant
        || lostWaitingQueueAccess
        || transferredAwayFromCurrentUser
      ) {
        let unavailableMessage = 'Este chamado não está mais disponível na sua fila.';

        if (claimedByAnotherAttendant) {
          unavailableMessage = 'Este chamado foi assumido por outro atendente.';
        } else if (transferredAwayFromCurrentUser) {
          unavailableMessage = 'Este chamado foi transferido para outro atendente.';
        }

        setCurrentChat(null);
        setMessages([]);
        setMessageAccess();
        showAlert(
          unavailableMessage,
          'warning',
          5000,
        );
        return;
      }

      if (isCurrentTicket) {
        setMessageAccess(buildMessageAccessFromTicketState(ticketState));
      }

      if (
        shouldRefetchMessagesForTicketState({
          currentChat: activeChat,
          previousAccess,
          ticketState,
        })
        && fetchMessages
      ) {
        await fetchMessages({ ...activeChat, ...ticketState }, 1);
      }
    };

    socket.on('ticketStateChanged', onTicketStateChanged);

    return () => {
      socket.off('ticketStateChanged', onTicketStateChanged);
    };
  }, [
    fetchMessages,
    setCurrentChat,
    setMessageAccess,
    setMessages,
    showAlert,
    socket,
    updateTicketMetadata,
    user?.id,
  ]);

  useEffect(() => {
    if (!socket) return undefined;

    const onUpdatedTicketsTransferred = (previousChat, notification, access) => {
      const activeCurrentChat = useTicketStore.getState().currentChat;

      upsertGeneralChat(notification, activeCurrentChat);

      if (
        activeCurrentChat?.sender === notification.sender
        && activeCurrentChat?.fromMsg === notification.fromMsg
      ) {
        setCurrentChat((previousCurrentChat) => (
          isSameTicket(previousCurrentChat, notification)
            ? { ...previousCurrentChat, ...notification }
            : previousCurrentChat
        ));
        setMessages((prevMessages) => {
          if (!notification.clientMessageId) {
            return [notification, ...prevMessages];
          }

          const hasOptimisticNotification = prevMessages.some(
            (message) => message.clientMessageId === notification.clientMessageId,
          );

          if (!hasOptimisticNotification) {
            return [notification, ...prevMessages];
          }

          return prevMessages.map((message) => (
            message.clientMessageId === notification.clientMessageId
              ? notification
              : message
          ));
        });
        setTimeout(handleScrollChatToBottom, 500);
      }

      syncWaitingQueueTicket(
        notification,
        resolveWaitingQueueVisibility(notification, access, user?.setores),
      );

      syncTransferredOpenTicket({
        previousTicket: previousChat,
        notification,
        currentUserId: user?.id,
      });
    };

    const onUpdatedTicketToClosed = (closedTicket, notification) => {
      upsertGeneralChat(notification, currentChat);

      if (areChatsEqual(currentChat, notification)) {
        setCurrentChat((prev) => ({
          ...prev,
          status: 3,
          body: notification.body,
          mediaType: notification?.mediaType,
          fromMe: notification.fromMe,
        }));
      }

      if (closedTicket?.status === 1) {
        setTotalUserChatsQueue((prev) => Math.max(0, prev - 1));
      }

      if (closedTicket?.status === 2 && closedTicket?.usuario_id === user.id) {
        setTotalOpenTickets((prev) => Math.max(0, prev - 1));
        setOpenTickets((prevTickets) => prevTickets
          .filter((chat) => chat?.id !== closedTicket?.id));
      }

      setUserChatsQueue((prevChatsQueue) => prevChatsQueue
        .filter((chat) => chat?.id !== closedTicket?.id));
    };

    socket.on('updatedTicketsTransferred', onUpdatedTicketsTransferred);
    socket.on('updatedTicketToClosed', onUpdatedTicketToClosed);

    return () => {
      socket.off('updatedTicketsTransferred', onUpdatedTicketsTransferred);
      socket.off('updatedTicketToClosed', onUpdatedTicketToClosed);
    };
  }, [
    currentChat,
    handleScrollChatToBottom,
    setCurrentChat,
    setMessages,
    setOpenTickets,
    setTotalOpenTickets,
    setTotalUserChatsQueue,
    setUserChatsQueue,
    socket,
    syncTransferredOpenTicket,
    syncWaitingQueueTicket,
    user,
    upsertGeneralChat,
  ]);

  useEffect(() => {
    if (!socket) return undefined;

    const onSessionConflict = (data) => {
      if (data.existingSession.userId === user.id) {
        setSessionConflict({
          showSessionConflict: true,
          sessionConflictData: data,
        });
      }
    };

    socket.on('sessionConflict', onSessionConflict);

    return () => {
      socket.off('sessionConflict', onSessionConflict);
    };
  }, [setSessionConflict, socket, user?.id]);

  useEffect(() => {
    if (!socket) return undefined;

    const onReceiveMessage = (previousChat, message, userId) => {
      const chatPayload = mergeChatMetadataWithMessage(previousChat, message);

      upsertGeneralChat(chatPayload, currentChat);

      if (areChatsEqual(currentChat, message) && user.id !== userId) {
        setMessages((prevMessages) => [message, ...prevMessages]);
        setTimeout(handleScrollChatToBottom, 500);
      }
    };

    const onReceiveComment = (previousChat, commentPayload) => {
      const chatPayload = mergeChatMetadataWithMessage(previousChat, commentPayload);

      upsertGeneralChat(chatPayload, currentChat);

      if (areChatsEqual(currentChat, commentPayload)) {
        setMessages((prevMessages) => [commentPayload, ...prevMessages]);
        setTimeout(handleScrollChatToBottom, 500);
      }
    };

    socket.on('receiveMessage', onReceiveMessage);
    socket.on('receiveComment', onReceiveComment);

    return () => {
      socket.off('receiveMessage', onReceiveMessage);
      socket.off('receiveComment', onReceiveComment);
    };
  }, [
    currentChat,
    handleScrollChatToBottom,
    setMessages,
    socket,
    user?.id,
    upsertGeneralChat,
    mergeChatMetadataWithMessage,
  ]);

  useEffect(() => {
    if (!recipientUser?.id) {
      setIsRecipientOnline(false);
      return;
    }

    const recipientOnline = onlineUsers?.some(
      (onlineUser) => recipientUser?.id === onlineUser.userId,
    );

    setIsRecipientOnline(Boolean(recipientOnline));
  }, [onlineUsers, recipientUser, setIsRecipientOnline]);

  useEffect(() => {
    if (!socket) return undefined;

    const onGetMessage = async (res) => {
      try {
        const userHasAccessToSenderOfMessage = Object.keys(user.senders).some(
          (key) => key === res.sender,
        );

        if (!userHasAccessToSenderOfMessage) return;

        const isChatOpen = currentChat?.fromMsg === res?.fromMsg
          && currentChat?.sender === res?.sender;

        if (isChatOpen) {
          const messagesWithMedia = await fetchMediaUrls(res);

          if (res.quotedMessage) {
            setQuotedMessages((prevQuotedMessages) => [
              ...prevQuotedMessages,
              res.quotedMessage,
            ]);
          }

          upsertGeneralChat(messagesWithMedia, currentChat);

          setMessages((prev) => [messagesWithMedia, ...prev]);
          if (res.fromMe === 0 && res.idMsg.startsWith('wamid')) {
            setOfficialApiTemplates((prevTemplates) => ({
              ...prevTemplates,
              isOfficialApiMessageExpired: false,
            }));
          }

          setTimeout(handleScrollChatToBottom, 500);

          if (currentChat.usuario_id === user.id) {
            const updatedOpenTickets = updateUserChatsWithNewMessage(
              openTickets,
              res,
              currentChat,
            );
            setOpenTickets(updatedOpenTickets);
          }

          if (!isRecordingAudio) {
            playOpenChatNotification();
          }
        }

        upsertGeneralChat(res, currentChat);

        const isForMyOpenTickets = openTickets.some(
          (ticket) => ticket.sender === res.sender && ticket.fromMsg === res.fromMsg,
        );

        if (isForMyOpenTickets) {
          const updatedOpenTickets = updateUserChatsWithNewMessage(
            openTickets,
            res,
            currentChat,
          );

          setOpenTickets(updatedOpenTickets);

          if (!isRecordingAudio) {
            playNotificationSound();
          }

          return;
        }

        const isAlreadyInQueue = userChatsQueue.some(
          (chat) => chat.sender === res.sender && chat.fromMsg === res.fromMsg,
        );

        if (isAlreadyInQueue) {
          const canViewInWaitingQueue = resolveWaitingQueueVisibility(
            res,
            res,
            user?.setores,
          );

          syncWaitingQueueTicket(res, canViewInWaitingQueue);
          if (
            canViewInWaitingQueue
            && res.fromMe !== 1
            && !isRecordingAudio
          ) {
            playNotificationSound();
          }

          return;
        }

        const canViewInWaitingQueue = resolveWaitingQueueVisibility(
          res,
          res,
          user?.setores,
        );

        if (Number(res.status) === 1 && canViewInWaitingQueue) {
          if (!isRecordingAudio) {
            playNotificationSound();
          }
          syncWaitingQueueTicket(res, true);
          return;
        }

        syncWaitingQueueTicket(res, false);
      } catch (error) {
        setErrorMessage('Algo deu errado! Entre em contato com o suporte.');
      }
    };

    socket.on('getMessage', onGetMessage);

    return () => {
      socket.off('getMessage', onGetMessage);
    };
  }, [
    currentChat,
    fetchMediaUrls,
    handleScrollChatToBottom,
    isRecordingAudio,
    openTickets,
    playNotificationSound,
    playOpenChatNotification,
    setErrorMessage,
    setMessages,
    setOfficialApiTemplates,
    setOpenTickets,
    setQuotedMessages,
    setTotalUserChatsQueue,
    setUserChatsQueue,
    socket,
    user,
    userChatsQueue,
    upsertGeneralChat,
  ]);
}
